import React, { useState, useEffect, useCallback } from 'react';
import { 
    ClipboardList, Trash2, Package, Search, 
    Layers, Plus, ChevronLeft, 
    Clock, CheckCircle, XCircle, Inbox, Archive, X, 
    Check, Hash, ThumbsUp, ArrowDownCircle, AlertCircle, Loader2, CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const BesoinsProduction = () => {
    const navigate = useNavigate();
    const [showForm, setShowForm] = useState(false);
    const [activeTab, setActiveTab] = useState('en_cours');
    const [historique, setHistorique] = useState([]);
    const [fichesTechniques, setFichesTechniques] = useState([]);
    const [produitsFinis, setProduitsFinis] = useState([]);
    const [produitsSemiFinis, setProduitsSemiFinis] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFT, setSelectedFT] = useState(null);
    const [quantite, setQuantite] = useState(1); // Ici quantite représente le NOMBRE DE LOTS
    const [panierBesoins, setPanierBesoins] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [numeroBesoin, setNumeroBesoin] = useState('');

    // --- ÉTAT DU TOAST INTÉGRÉ ---
    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success', // 'success', 'error', 'confirm'
        message: '',
        onConfirm: null
    });

    const primaryColor = '#2563eb';
    const borderColor = '#e2e8f0';

    // --- LOGIQUE DE CHARGEMENT ---
    const fetchHistorique = useCallback(async () => {
        try {
            const res = await API.get('/production-needs'); 
            setHistorique(res.data || []);
        } catch (err) { 
            console.error("Erreur historique:", err); 
        }
    }, []);

   const fetchInitialData = useCallback(async () => {
        try {
            const [ftRes, prodRes, psfRes] = await Promise.all([
                API.get('/technical-sheets').catch(() => ({ data: [] })),
                API.get('/products').catch(() => ({ data: [] })),
                API.get('/semi-finished-products').catch(() => ({ data: [] }))
            ]);

            const produitsFinisList = Array.isArray(prodRes.data) ? prodRes.data.filter(p => p.is_active === 1) : [];
            const produitsSemiFinisList = Array.isArray(psfRes.data) ? psfRes.data.filter(p => p.is_active === 1) : [];

            const fichesEligibles = (ftRes.data || []).map(ft => {
                let uniteTrouvee = ft.unite_mesure || ft.unite;
                if (!uniteTrouvee) {
                    if (ft.product_id) {
                        const produitFini = produitsFinisList.find(p => p.id === ft.product_id);
                        uniteTrouvee = produitFini?.unite_mesure || produitFini?.unite_code || produitFini?.unite;
                    } else if (ft.semi_fini_id) {
                        const psf = produitsSemiFinisList.find(s => s.id === ft.semi_fini_id);
                        uniteTrouvee = psf?.unite_mesure || psf?.unite_code || psf?.unite;
                    }
                }
                return {
                    ...ft,
                    unite_affichage: (uniteTrouvee || 'UNITE').toUpperCase(),
                    quantite_base: Number(ft.quantite_base) > 0 ? Number(ft.quantite_base) : 1
                };
            }).filter(ft => ft.is_active === 1 && (ft.semi_fini_id || ft.product_id));
            
            setFichesTechniques(fichesEligibles);
            setProduitsFinis(produitsFinisList);
            setProduitsSemiFinis(produitsSemiFinisList);
        } catch (err) { 
            showToast("Erreur de chargement des données", "error");
        }
    }, []);

    // --- FONCTION DE SECOURS POUR RÉCUPÉRER LE VRAI NOM ---
    const getNomAffichage = (ft) => {
        if (ft.nom && ft.nom.trim() !== '' && ft.nom.toLowerCase() !== 'non lié') {
            return ft.nom;
        }
        if (ft.product_id) {
            const p = produitsFinis.find(item => item.id === ft.product_id);
            return p ? p.nom : (ft.nom_produit || 'Produit Fini');
        }
        if (ft.semi_fini_id) {
            const s = produitsSemiFinis.find(item => item.id === ft.semi_fini_id);
            return s ? s.nom : (ft.nom_produit || 'Produit Semi-Fini');
        }
        return ft.nom_produit || 'Article sans nom';
    };

    useEffect(() => {
        if (!socket) return;
        socket.on('PRODUCTION_NEED_UPDATED', () => fetchHistorique());
        socket.on('REFRESH_UI', (data) => {
            if (data?.module === 'PRODUCTION') fetchHistorique();
        });
        return () => {
            socket.off('PRODUCTION_NEED_UPDATED');
            socket.off('REFRESH_UI');
        };
    }, [fetchHistorique]);

    useEffect(() => {
        fetchHistorique();
        fetchInitialData();
    }, [fetchHistorique, fetchInitialData]);

    // --- SYSTÈME DE NOTIFICATION TOAST ---
    useEffect(() => {
        if (notification.isOpen && notification.type !== 'confirm') {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen, notification.type]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message, onConfirm: null });
    };

    const showConfirm = (message, onConfirm) => {
        setNotification({ isOpen: true, type: 'confirm', message, onConfirm });
    };

    // --- ACTIONS ---
    const executerAnnulation = async (id_ligne) => {
        try {
            await API.delete(`/production-needs/${id_ligne}`); 
            setNotification({ isOpen: false });
            showToast("Besoin annulé avec succès");
            fetchHistorique();
        } catch (err) { 
            const msg = err.response?.data?.error || "Erreur lors de la suppression";
            showToast(msg, "error"); 
        }
    };

    const ouvrirFormulaire = () => {
        setNumeroBesoin(`PANIER-${Date.now().toString().slice(-4)}`);
        setShowForm(true);
    };

    const ajouterAuPanier = () => {
        const nbLots = Math.round(parseFloat(quantite) * 1000) / 1000;
        if (!selectedFT) return showToast("Sélectionnez un article", "error");
        if (isNaN(nbLots) || nbLots <= 0) return showToast("Nombre de lots invalide", "error");
        
        // Calcul du volume total réel = Nombre de lots x Quantité de base de la FT
        const qteTotaleProduite = nbLots * selectedFT.quantite_base;
        
        const idLigne = `BS-${Date.now().toString().slice(-6)}`;
        const uniteADonner = selectedFT.unite_affichage || 'UNITE';
        const nomFinal = getNomAffichage(selectedFT);

        setPanierBesoins([...panierBesoins, { 
            id: idLigne, 
            ft_id: selectedFT.id, 
            semi_fini_id: selectedFT.semi_fini_id,
            nom: nomFinal, 
            qte: qteTotaleProduite, // Volume total envoyé au magasin
            nombre_lots: nbLots,
            quantite_base: selectedFT.quantite_base,
            unite: uniteADonner 
        }]);
        
        setSelectedFT(null);
        setQuantite(1);
        showToast(`Ajouté : ${nbLots} lot(s) -> ${qteTotaleProduite} ${uniteADonner}`);
    };

    const envoyerAuMagasin = async () => {
        if (panierBesoins.length === 0) return showToast("Le panier est vide", "error");
        setIsSubmitting(true);
        try {
            const codeGroupe = `GP-${Date.now().toString().slice(-6)}`;
            const envois = panierBesoins.map(item => {
                return API.post('/production-needs', {
                    id: item.id,
                    code_groupe: codeGroupe, 
                    ft_id: item.ft_id,
                    article_nom: item.nom,
                    type_article: item.semi_fini_id ? 'PSF' : 'PF',
                    quantite: parseFloat(item.qte), // Envoi de la quantité totale calculée
                    unite: item.unite,
                    observation_production: `Lots: ${item.nombre_lots} (Base FT: ${item.quantite_base})`, 
                    observation_magasin: ""
                });
            });
            await Promise.all(envois);
            showToast("Transmis au magasin");
            setPanierBesoins([]);
            setShowForm(false);
            fetchHistorique();
        } catch (err) { 
            const msg = err.response?.data?.error || "Erreur lors de l'envoi";
            showToast(msg, "error"); 
        } finally { 
            setIsSubmitting(false); 
        }
    };

    // --- FILTRAGE ---
    const besoinsEnCours = historique.filter(h => 
        ['EN_ATTENTE', 'EN_ATTENTE_DE_VALIDATION', 'EN_PRODUCTION', 'ENCOURS_DE_PRODUCTION'].includes(h.statut_ligne)
    );
    const besoinsValides = historique.filter(h => h.statut_ligne === 'VALIDE');
    const besoinsTermines = historique.filter(h => ['TERMINE', 'TERMINER'].includes(h.statut_ligne));
    const besoinsRejetes = historique.filter(h => ['REJETE', 'REFUSE'].includes(h.statut_ligne));
    const besoinsAnnules = historique.filter(h => h.statut_ligne === 'ANNULE');

    const getListeAffichee = () => {
        switch(activeTab) {
            case 'en_cours': return besoinsEnCours;
            case 'valides': return besoinsValides;
            case 'termines': return besoinsTermines;
            case 'rejetes': return besoinsRejetes;
            case 'archives': return besoinsAnnules;
            default: return besoinsEnCours;
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            'EN_ATTENTE': { bg: '#fef3c7', text: '#b45309', icon: <Clock size={12}/>, label: 'EN ATTENTE' },
            'EN_ATTENTE_DE_VALIDATION': { bg: '#fef3c7', text: '#b45309', icon: <Clock size={12}/>, label: 'À VALIDER' },
            'VALIDE': { bg: '#dcfce7', text: '#15803d', icon: <CheckCircle size={12}/>, label: 'VALIDÉ' },
            'EN_PRODUCTION': { bg: '#e0f2fe', text: '#0369a1', icon: <Layers size={12}/>, label: 'EN PROD' },
            'ENCOURS_DE_PRODUCTION': { bg: '#e0f2fe', text: '#0369a1', icon: <Layers size={12}/>, label: 'EN PROD' },
            'ANNULE': { bg: '#f1f5f9', text: '#475569', icon: <X size={12}/>, label: 'ANNULÉ' },
            'TERMINE': { bg: '#d1fae5', text: '#065f46', icon: <Package size={12}/>, label: 'TERMINÉ' },
            'REJETE': { bg: '#fee2e2', text: '#dc2626', icon: <XCircle size={12}/>, label: 'REJETÉ' },
        };
        const st = styles[status] || { bg: '#f1f5f9', text: '#64748b', icon: null, label: status };
        return (
            <span style={{ background: st.bg, color: st.text, padding: '5px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                {st.icon} {st.label}
            </span>
        );
    };

    const s = {
        page: { display: 'flex', background: '#f8fafc', height: '100vh', width: '100%', overflow: 'hidden', position: 'relative' },
        main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        header: { background: '#fff', padding: '15px 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        tabBar: { display: 'flex', gap: '20px', padding: '0 30px', background: '#fff', borderBottom: '1px solid #e2e8f0', overflowX: 'auto' },
        tab: (active, color = '#2563eb') => ({ padding: '15px 5px', fontSize: '12px', fontWeight: '800', color: active ? color : '#94a3b8', borderBottom: active ? `3px solid ${color}` : '3px solid transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }),
        content: { flex: 1, padding: '25px', overflowY: 'auto' },
        btnNew: { background: '#2563eb', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
        btnReception: { background: '#f8fafc', color: '#1e293b', border: '1px solid #e2e8f0', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
        btnBack: { background: '#7f1d1d', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '900', fontSize: '15px', marginBottom: '25px', display: 'inline-flex', alignItems: 'center', gap: '12px' },
        table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
        th: { textAlign: 'left', padding: '15px', fontSize: '11px', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase' },
        td: { padding: '15px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#1e293b' },
        gridForm: { display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '20px', height: 'calc(100% - 80px)' },
        
        toastContainer: {
            position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, display: 'flex', alignItems: 'center',
            background: 'white', padding: '12px 20px', borderRadius: '15px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            border: `1px solid ${borderColor}`,
            minWidth: '400px',
            transition: 'all 0.3s ease'
        },
        confirmBtn: { background: '#dc2626', color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' },
        cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }
    };

    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const isConfirm = notification.type === 'confirm';
        const color = notification.type === 'error' ? '#ef4444' : (isConfirm ? '#f59e0b' : '#10b981');
        return (
            <div style={{ ...s.toastContainer, borderLeft: `8px solid ${color}` }}>
                <div style={{ 
                    display: 'flex', justifyContent: 'center', alignItems: 'center', 
                    width: '35px', height: '35px', borderRadius: '50%', 
                    background: `${color}15`, marginRight: '15px', flexShrink: 0
                }}>
                    {notification.type === 'error' ? <AlertCircle size={20} color={color} /> : 
                     isConfirm ? <Trash2 size={20} color={color} /> : 
                     <CheckCircle2 size={20} color={color} />}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{notification.message}</p>
                    {isConfirm && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button onClick={notification.onConfirm} style={s.confirmBtn}>SUPPRIMER</button>
                            <button onClick={() => setNotification({isOpen: false})} style={s.cancelBtn}>ANNULER</button>
                        </div>
                    )}
                </div>
                {!isConfirm && (
                    <button onClick={() => setNotification({isOpen: false})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}>
                        <X size={20} />
                    </button>
                )}
            </div>
        );
    };

    return (
        <div style={s.page}>
            <Sidebar />
            <div style={s.main}>
                <NotificationToast />
                <header style={s.header}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '10px' }}>
                            <ClipboardList size={28} color="#2563eb" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '900' }}>SUIVI DES BESOINS</h2>
                            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Gestion des flux Production / Magasin</p>
                        </div>
                    </div>
                    {!showForm && (
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => navigate('/gestion_analytique/reception')} style={s.btnReception}>
                                <ArrowDownCircle size={20} /> RÉCEPTION PRODUCTION
                            </button>
                            <button onClick={ouvrirFormulaire} style={s.btnNew}>
                                <Plus size={20} /> NOUVEAU BESOIN
                            </button>
                        </div>
                    )}
                </header>

                {!showForm && (
                    <div style={s.tabBar}>
                        <div onClick={() => setActiveTab('en_cours')} style={s.tab(activeTab === 'en_cours')}>
                            <Inbox size={18} /> EN COURS ({besoinsEnCours.length})
                        </div>
                        <div onClick={() => setActiveTab('valides')} style={s.tab(activeTab === 'valides', '#15803d')}>
                            <ThumbsUp size={18} /> VALIDÉS ({besoinsValides.length})
                        </div>
                        <div onClick={() => setActiveTab('termines')} style={s.tab(activeTab === 'termines', '#059669')}>
                            <CheckCircle size={18} /> TERMINÉS ({besoinsTermines.length})
                        </div>
                        <div onClick={() => setActiveTab('rejetes')} style={s.tab(activeTab === 'rejetes', '#dc2626')}>
                            <XCircle size={18} /> REJETÉS ({besoinsRejetes.length})
                        </div>
                        <div onClick={() => setActiveTab('archives')} style={s.tab(activeTab === 'archives', '#64748b')}>
                            <Archive size={18} /> ANNULÉS ({besoinsAnnules.length})
                        </div>
                    </div>
                )}

                <div style={s.content}>
                    {!showForm ? (
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Réf Ligne</th>
                                    <th style={s.th}>Article</th>
                                    <th style={s.th}>Quantité</th>
                                    <th style={s.th}>Statut</th>
                                    <th style={s.th}>Obs. Magasin</th>
                                    <th style={{ ...s.th, textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getListeAffichee().length > 0 ? getListeAffichee().map((h) => (
                                    <tr key={h.id}>
                                        <td style={s.td}>
                                            <div style={{ fontWeight: 'bold', color: '#2563eb' }}>{h.id}</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{new Date(h.date_emission).toLocaleString('fr-FR')}</div>
                                        </td>
                                        <td style={s.td}>
                                            <div style={{ fontWeight: '900' }}>{h.article_nom}</div>
                                            <div style={{ fontSize: '10px', color: '#64748b' }}><Hash size={10} style={{display:'inline'}}/> {h.code_groupe}</div>
                                        </td>
                                        <td style={s.td}>
                                            <span style={{ fontWeight: 'bold', background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px' }}>{h.quantite} {h.unite}</span>
                                        </td>
                                        <td style={s.td}>{getStatusBadge(h.statut_ligne)}</td>
                                        <td style={{ ...s.td, fontSize: '11px', fontStyle: 'italic' }}>{h.observation_magasin || h.observation_production || '---'}</td>
                                        <td style={s.td}>
                                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                                {h.statut_ligne === 'EN_ATTENTE' ? (
                                                    <button onClick={() => showConfirm(`Confirmer la suppression du besoin ${h.id} ?`, () => executerAnnulation(h.id))} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', width: '34px', height: '34px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Trash2 size={18} />
                                                    </button>
                                                ) : <CheckCircle size={18} style={{ color: '#cbd5e1' }} />}
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="6" style={{ padding: '50px', textAlign: 'center', color: '#94a3b8' }}>Aucun besoin trouvé.</td></tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ height: '100%' }}>
                            <button onClick={() => setShowForm(false)} style={s.btnBack}><ChevronLeft size={18} strokeWidth={3} /> RETOUR AU SUIVI</button>
                            <div style={s.gridForm}>
                                <div style={{ background: '#fff', padding: '20px', borderRadius: '15px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ position: 'relative', marginBottom: '15px' }}>
                                        <Search size={18} style={{ position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' }} />
                                        <input style={{ width: '100%', padding: '10px 40px', borderRadius: '8px', border: '1px solid #cbd5e1' }} placeholder="Chercher un article..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                    </div>
                                    <div style={{ flex: 1, overflowY: 'auto' }}>
                                        {fichesTechniques
                                            .filter(ft => getNomAffichage(ft).toLowerCase().includes(searchTerm.toLowerCase()))
                                            .map(ft => {
                                                const nomAffichage = getNomAffichage(ft);
                                                return (
                                                    <div key={ft.id} onClick={() => setSelectedFT(ft)} style={{ padding: '12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: selectedFT?.id === ft.id ? '#eff6ff' : 'transparent', borderRadius: '8px' }}>
                                                        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{nomAffichage}</div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                                            <span style={{ fontSize: '10px', color: '#64748b' }}>{ft.semi_fini_id ? 'SEMI-FINI' : 'PRODUIT FINI'} (Base: {ft.quantite_base})</span>
                                                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#0284c7' }}>Unité : {ft.unite_affichage}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>

                                    {selectedFT && (
                                        <div style={{ marginTop: '15px', background: '#1e293b', padding: '15px', borderRadius: '12px', color: '#fff' }}>
                                            <div style={{ fontSize: '11px', marginBottom: '8px', fontWeight: 'bold', color: '#38bdf8', display: 'flex', justifyContent: 'space-between' }}>
                                                <span>NOMBRE DE LOTS À PRODUIRE :</span>
                                                <span style={{ color: '#fbbf24' }}>Base FT : {selectedFT.quantite_base} {selectedFT.unite_affichage}</span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                <input 
                                                    type="number" 
                                                    step="any"
                                                    min="0.0001"
                                                    value={quantite} 
                                                    onChange={(e) => setQuantite(e.target.value)} 
                                                    style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', color: '#000', fontWeight: 'bold', fontSize: '16px' }} 
                                                />
                                                <div style={{ background: '#334155', padding: '10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', color: '#34d399', whiteSpace: 'nowrap' }}>
                                                    = {(parseFloat(quantite || 0) * selectedFT.quantite_base)} {selectedFT.unite_affichage}
                                                </div>
                                                <button onClick={ajouterAuPanier} style={{ background: '#fbbf24', padding: '10px 20px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>AJOUTER</button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div style={{ background: '#fff', padding: '20px', borderRadius: '15px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ fontWeight: '900', marginBottom: '15px', color: '#1e293b' }}>LISTE DES BESOINS ({numeroBesoin})</div>
                                    <div style={{ flex: 1, overflowY: 'auto' }}>
                                        {panierBesoins.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Panier vide</div>
                                        ) : panierBesoins.map((item, idx) => (
                                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                                <div>
                                                    <div style={{fontWeight: 'bold', fontSize: '13px'}}>{item.nom}</div>
                                                    <div style={{fontSize: '11px', color: '#94a3b8'}}>Lots : {item.nombre_lots} (Base: {item.quantite_base})</div>
                                                    <div style={{fontSize: '13px', color: '#2563eb', fontWeight: '900'}}>Volume Total : {item.qte} {item.unite}</div>
                                                </div>
                                                <button onClick={() => setPanierBesoins(panierBesoins.filter((_, i) => i !== idx))} style={{background: '#fee2e2', border: 'none', padding: '8px', borderRadius: '6px', cursor: 'pointer'}}><Trash2 size={16} color="#dc2626" /></button>
                                            </div>
                                        ))}
                                    </div>
                                    <button onClick={envoyerAuMagasin} disabled={panierBesoins.length === 0 || isSubmitting} style={{ width: '100%', background: '#059669', color: '#fff', border: 'none', padding: '15px', borderRadius: '12px', fontWeight: '900', cursor: 'pointer', marginTop: '15px', opacity: (panierBesoins.length === 0 || isSubmitting) ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                        {isSubmitting ? <><Loader2 size={20} className="animate-spin" /> ENVOI EN COURS...</> : "TRANSMETTRE AU MAGASIN"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BesoinsProduction;